import { useCallback, useEffect, useRef, useState } from "react";
import { Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "@/lib/api";
import { OrderActions, OrderCard, GiftBadge, DueNote, PayPalNote } from "@/admin/OrderParts";
import { PageTitle, Btn, Sel, StatusBadge } from "@/admin/ui";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [status, setStatus] = useState("");
  const seq = useRef(0);
  const load = useCallback(() => {
    const n = ++seq.current;
    return api.get("/admin/orders", { params: { q: q || undefined, status: status || undefined } }).then((r) => n === seq.current && setOrders(r.data));
  }, [q, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const [busy, setBusy] = useState("");
  const act = async (path, msg) => { if (busy) return; setBusy(path); try { await api.post(path); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  return (
    <div>
      <PageTitle title="Orders" sub="Razorpay orders are marked paid automatically after verification. PayPal.Me orders are marked paid by you — check PayPal, then tap Mark as paid.">
        <Btn variant="ghost" onClick={() => downloadFile("/admin/orders/export", "namonics_orders.csv")} data-testid="orders-export-btn"><Download className="h-4 w-4" /> Export CSV</Btn>
      </PageTitle>
      <div className="mb-4 flex flex-wrap gap-2">
        <Input placeholder="Search name, email, reference" value={q} onChange={(e) => setQ(e.target.value)} className="w-full sm:max-w-xs" data-testid="orders-search-input" />
        <Sel value={status} onChange={setStatus} options={[["", "All statuses"], ["pending_payment", "Awaiting payment"], ["awaiting_confirmation", "PayPal to confirm"], ["paid", "Paid"], ["failed", "Failed"], ["cancelled", "Cancelled"], ["refunded", "Refunded"]]} testId="orders-status-filter" />
      </div>
      <div className="space-y-3 md:hidden" data-testid="orders-cards">
        {orders.map((o) => <OrderCard key={o.id} o={o} busy={busy} act={act} />)}
        {!orders.length && <p className="py-10 text-center text-muted-foreground">No matching orders.</p>}
      </div>
      <div className="hidden overflow-x-auto rounded-md border border-border md:block">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead className="bg-secondary/50 text-left text-xs uppercase text-muted-foreground">
            <tr>{["Reference / Date", "Customer", "Amount", "Payment", "Report", "Delivery", "Refund", ""].map((h, i) => <th key={i} className="px-3 py-2">{h}</th>)}</tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-border" data-testid={`order-row-${o.ref}`}>
                <td className="px-3 py-2 font-mono-ui text-xs">{o.ref}<span className="block text-muted-foreground">{o.created_at.slice(0, 16).replace("T", " ")}</span></td>
                <td className="px-3 py-2">{o.display_name}<GiftBadge o={o} /><span className="block max-w-[14rem] truncate text-xs text-muted-foreground">{o.email}</span></td>
                <td className="px-3 py-2">{o.currency} {o.amount}{o.promo_code && <span className="block text-[10px] text-muted-foreground">{o.promo_code}</span>}</td>
                <td className="px-3 py-2"><StatusBadge s={o.payment_status} /><PayPalNote o={o} /></td>
                <td className="px-3 py-2"><StatusBadge s={o.report_status} /></td>
                <td className="px-3 py-2"><StatusBadge s={o.email_status} /><DueNote o={o} /></td>
                <td className="px-3 py-2"><StatusBadge s={o.refund_status} /></td>
                <td className="whitespace-nowrap px-3 py-2 text-xs"><OrderActions o={o} busy={busy} act={act} /></td>
              </tr>
            ))}
            {!orders.length && <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">No matching orders.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
